/**
 * Continuing-education credit for the convention, as AACS defines it.
 *
 * Every rule here is read off the AACS Continuing Education Attendance Voucher
 * that WVCEA supplied on 6 August 2026, form version 09.2020. Nothing is
 * inferred: where the form is silent, this code is silent too, and the teacher
 * is asked instead.
 *
 * The form belongs to the teacher. She lists the sessions she attended, signs
 * it, has an administrator sign to verify she was there, keeps it, and mails it
 * with her certification renewal when that falls due. The software may share
 * the session list with her school for review, but the official voucher remains
 * the teacher's document.
 */

/** "1 contact hour per 50-minute session." */
export const CONTACT_HOURS_PER_SESSION = 1;

/** "The maximum number of contact hours allowed per convention is 10." */
export const MAX_CONTACT_HOURS_PER_CONVENTION = 10;

/**
 * The nine workshop categories printed on the form, in its own order and its own
 * words. The number is what a teacher writes in the "#" column.
 */
export interface WorkshopCategory {
  number: number;
  label: string;
  /** The examples the form prints in smaller type beside two of the categories. */
  note: string | null;
}

export const WORKSHOP_CATEGORIES: readonly WorkshopCategory[] = [
  { number: 1, label: "Preschool", note: null },
  { number: 2, label: "Kindergarten", note: null },
  { number: 3, label: "Elementary", note: null },
  { number: 4, label: "Secondary", note: null },
  { number: 5, label: "All-level", note: "Music, Art, PE" },
  { number: 6, label: "Special Education", note: null },
  { number: 7, label: "Specialist", note: "Bible, Computer, Counseling" },
  { number: 8, label: "Administration", note: null },
  { number: 9, label: "General Education", note: null },
];

export type WorkshopCategoryNumber = number;

/** What a schedule item has to be for the form to allow it on the list. */
export type CreditableKind = "instructional" | "general_session" | "meeting" | "special";

/**
 * Whether a session may be listed for credit.
 *
 * "Credit will not be granted for general preaching sessions." The three general
 * sessions are the convention's preaching, so they earn nothing. Neither do the
 * meetings, lunch, the Idea Exchange or the exhibit hall: the form asks for
 * sessions attended in an endorsed area, and none of those is a workshop.
 *
 * That leaves the six seminar blocks, which is why the ten-hour ceiling never
 * binds here — the most anybody can list at this convention is six.
 */
export function earnsContactHours(kind: CreditableKind): boolean {
  return kind === "instructional";
}

/**
 * The category this session most likely belongs to, from the audience WVCEA
 * published for it.
 *
 * A proposal, never a verdict. The form says credit is granted when the workshop
 * is "in your endorsed area or the area of general education", and the software
 * does not know what anybody is endorsed in — so every row can be changed by the
 * teacher before she prints it. Where the audience does not clearly map, this
 * returns General Education, which is the category the form allows to everybody.
 */
export function proposedCategory(audienceLabels: readonly string[]): WorkshopCategoryNumber {
  const text = audienceLabels.join(" ").toLocaleLowerCase("en-US");
  const has = (term: string) => text.includes(term);

  // Read most specific first. An audience naming two spheres — "Secondary •
  // Administration" — proposes the narrower teaching one, because a teacher
  // endorsed in administration can change it and one endorsed in secondary
  // cannot guess.
  if (has("coach")) return 5;
  if (has("k4") || has("k5") || has("kindergarten")) return 2;
  if (has("preschool")) return 1;
  if (has("elementary") || has("prek") || has("grades 3")) return 3;
  if (has("secondary")) return 4;
  if (has("administration")) return 8;
  return 9;
}

export interface VoucherLine {
  itemId: string;
  title: string;
  category: WorkshopCategoryNumber;
}

/**
 * The total the form asks for at the foot of the table, held to the ceiling it
 * prints. Anything above ten is the teacher's to resolve, not ours to discard
 * quietly, so the count of what was listed comes back beside the capped total.
 */
export interface VoucherTotal {
  listed: number;
  contactHours: number;
  cappedAtMaximum: boolean;
}

export function voucherTotal(lines: readonly VoucherLine[]): VoucherTotal {
  const listed = lines.length;
  const earned = listed * CONTACT_HOURS_PER_SESSION;
  return {
    listed,
    contactHours: Math.min(earned, MAX_CONTACT_HOURS_PER_CONVENTION),
    cappedAtMaximum: earned > MAX_CONTACT_HOURS_PER_CONVENTION,
  };
}
